import React, { useEffect, useMemo } from 'react';
import { cn } from '@/lib/utils';
import useUserStore from '@/lib/hooks/zustand-stores/use-user-store'
import useWebsocketStore from '@/lib/hooks/zustand-stores/use-websocket-store'
import { AnimatePresence, motion } from 'framer-motion';
import { useWSMessage } from '@/lib/hooks/use-ws-message';
import { logger } from '@/lib/logger';

interface OnlineStatusProps {
  userId: string;
  pulse?: boolean;
  position?: 'bottom-right' | 'center' | 'inline';
  layout?: 'absolute' | 'static';
  className?: string;
  onlineDotSize?: number;
  isVerified?: string | null;
  variant?: 'dot' | 'text';
  text?: string;
  blink?: boolean;
}

/**
 * OnlineStatus component
 * Shows a visual indicator of a user's online status.
 * Uses the new WebSocket architecture for requesting and displaying online status.
 */
export const OnlineStatus = React.memo(({
  userId,
  pulse = false,
  position = 'bottom-right',
  layout = 'absolute',
  className,
  onlineDotSize = 3,
  isVerified,
  variant = 'dot',
  text = 'Active Now',
  blink = false
}: OnlineStatusProps) => {
  // Use the new WebSocket message hook for sending requests
  const { sendMessage, isConnected } = useWSMessage();
  // Still use the store for checking online status
  const isOnline = useWebsocketStore(state => state.isOnline)
  const mixusUserId = useUserStore(state => state.user?._id)

  useEffect(() => {
    if (mixusUserId !== userId && isConnected) {
      try {
        // Use the new message format
        sendMessage('requestUserStatus', { userId }, { showToasts: false });
        logger.debug('Requested user status', { userId });
      } catch (error) {
        logger.error('Failed to request user status', { error, userId });
      }
    }
  }, [mixusUserId, userId, sendMessage, isConnected]);

  const isUserOnline = useMemo(() => 
    mixusUserId === userId || (isOnline && isOnline(userId)), 
    [mixusUserId, userId, isOnline]
  );

  if (!isUserOnline) return null;

  if (variant === 'text') {
    return (
      <AnimatePresence>
        <motion.div
          initial={{ scale: 0.95, opacity: 0.9 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0.9 }}
          transition={{
            duration: 0.2,
            ease: 'easeInOut'
          }}
          className={cn(
            'animate-subtle-pop inline-flex items-center gap-1.5',
            'rounded-full bg-mixus-forest-green/10 dark:bg-mixus-forest-green/30 px-2 py-0.5',
            'text-xs font-medium text-mixus-forest-green dark:text-mixus-forest-green',
            pulse && "animate-pulse",
            className
          )}
          aria-label="Online Status"
        >
          <span className={cn(
            "w-1.5 h-1.5 rounded-full bg-mixus-forest-green",
            blink && "animate-online-status-blink"
          )} />
          {text}
        </motion.div>
      </AnimatePresence>
    );
  }

  const dotPositionClass = position === 'inline' ? '' :
    isVerified ? '-bottom-0.5 -right-0.5' :
    position === 'center' ? 'inset-0 m-auto' : '-bottom-0.5 -right-0.5';

  return (
    <AnimatePresence>
      <motion.div
        initial={{ scale: 0.95, opacity: 0.9 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0.9 }}
        transition={{
          duration: 0.2,
          ease: 'easeInOut'
        }}
        className={cn(
          'animate-subtle-pop',
          layout === 'absolute' && "absolute",
          dotPositionClass,
          `w-${onlineDotSize} h-${onlineDotSize} rounded-full`,
          "bg-mixus-forest-green",
          "border-1.5 border-white dark:border-black",
          "shadow-sm",
          blink && "animate-online-status-blink",
          pulse && "animate-pulse",
          className
        )}
        aria-label="Online Status"
      />
    </AnimatePresence>
  );
});

OnlineStatus.displayName = 'OnlineStatus';
